// apps/web/src/features/agent/artifacts/ChartArtifactCard.tsx
//
// Renders model-produced chart artifacts as controlled inline SVG. The model
// supplies only data; React owns every SVG element and attribute.
//
// INVARIANT (XSS): model strings render only as JSX/SVG text nodes (`text` and
// `title`). No `foreignObject`, HTML parser, markdown parser, event attributes,
// or model-provided JS can be introduced here.
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../ui/index.js';
import { ChartDataTable } from './ChartDataTable.js';
import { ChartMetadata } from './ChartMetadata.js';
import { ChartLegend, ChartSvg } from './ChartSvg.js';
import { buildChartModel, isSupportedChartType, type ChartArtifact } from './chartModel.js';

export function ChartArtifactCard({ artifact }: { artifact: ChartArtifact }) {
  const { i18n, t } = useTranslation('agent');
  const titleId = useId();
  const model = useMemo(() => buildChartModel(artifact), [artifact]);
  const formatter = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  if (!isSupportedChartType(artifact.chartType)) {
    return (
      <div style={{ fontSize: 'var(--fz-13)', color: 'var(--fg-3)' }}>
        {t('artifacts.unsupported')}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
        <span style={{ fontSize: 'var(--fz-13)', fontWeight: 600, color: 'var(--fg-1)' }}>
          {artifact.title}
        </span>
        {artifact.description !== undefined ? (
          <span style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-2)' }}>
            {artifact.description}
          </span>
        ) : null}
      </div>

      {model.labels.length === 0 ? (
        <EmptyState icon="bar-chart-3" text={t('artifacts.chart.empty')} />
      ) : (
        <>
          <ChartSvg
            artifact={artifact}
            model={model}
            titleId={titleId}
            format={formatter.format}
            tooltip={(series, x, y) =>
              t('artifacts.chart.tooltip', { series, x, y: formatter.format(y) })
            }
          />
          <ChartLegend series={model.series.map((s) => s.name)} />
          <ChartMetadata artifact={artifact} locale={i18n.language} />
          <ChartDataTable labels={model.labels} series={model.series} format={formatter.format} />
        </>
      )}
    </div>
  );
}
